import React, { useRef, useState } from "react";
import contact from "../../../assets/images/contact-us.png";
import { IoMdMail } from "react-icons/io";
import { FaPhoneAlt } from "react-icons/fa";
import { Button, Checkbox, Form, Input, message } from "antd";
import SendIcon from "../../../global/components/Icons/SendIcon";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const ContactWrapper = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const agreed = Form.useWatch("agree", form);
  const captchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleFinish = async (values) => {
    if (!captchaToken) {
      message.error("Please complete the captcha.");
      return;
    }
    const { agree, ...payload } = values;
    try {
      setLoading(true);
      const res = await fetch("/api/send-contact-mail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstname: payload.firstname,
          lastname: payload.lastname,
          email: payload.email,
          message: payload.message,
          ...(payload.phone ? { phone: payload.phone } : {}),
          captcha_token: captchaToken,
        }),
      });
      if (!res.ok) {
        throw new Error("Failed to send");
      }
      message.success(
        "Your message has been sent. We'll get back to you soon.",
      );
      form.resetFields();
      captchaRef.current?.resetCaptcha();
      setCaptchaToken(null);
    } catch (err) {
      message.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="max-w-[1280px] mx-auto px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Left: Info */}
        <div className="animate-fadeIn">
          <div className="space-y-0 w-full h-full flex flex-col">
            {/* Image Section with Overlay Text */}
            <div className="relative overflow-hidden flex-1">
              <img
                src={contact}
                alt="Contact Us"
                className="w-full h-full object-cover min-h-[500px] sm:min-h-[400px]"
              />

              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80"></div>

              {/* Content Overlay */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-8 text-white">
                <h2 className="text-[20px] sm:text-[24px] font-bold mb-4 font-['Poppins'] leading-tight">
                  NEED DESIGN ASSISTANCE WITH OUR PRODUCT?
                </h2>
                <p className="text-[13px] sm:text-[14px] leading-relaxed text-justify mb-8 opacity-90 max-w-md">
                  Sicunet staff has years of experience in system design. Our
                  knowledge covers most applications, utilizing a variety of
                  technologies while ensuring safety and code compliance.
                  <br />
                  We are here to help you answer any questions you may have—and
                  some that you may not know to ask.
                  <br />
                  Our assistance will ensure we meet your project requirements
                  at the lowest possible cost.
                </p>
                {/* Contact Info in Overlay */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-white backdrop-blur-sm p-2.5 rounded-full">
                      <IoMdMail size={20} className="text-gray-500" />
                    </div>
                    <span className="text-[14px]">sales@sicunet.com</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-white backdrop-blur-sm p-2.5 rounded-full">
                      <FaPhoneAlt size={16} className="text-gray-500" />
                    </div>
                    <span className="text-[14px]">857-346-0130</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Form */}
        <div className="animate-fadeIn flex items-center justify-center">
          <div className="bg-white py-6 px-6 sm:px-8 lg:px-10 max-w-full w-full mx-auto h-full flex flex-col justify-center">
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-[24px] text-center font-bold text-[#121212] font-['Poppins'] mb-3">
                GET IN TOUCH
              </h2>
              <p className="text-[14px] sm:text-[14px] leading-relaxed   max-w-md text-justify">
                Please complete the form below, and our team will respond within
                24 hours with the necessary information
              </p>
            </div>

            {/* Form */}
            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              onFinish={handleFinish}
              className="space-y-1"
            >
              <Form.Item
                label={
                  <span className="text-[#121212] text-[14px] font-medium">
                    First Name *
                  </span>
                }
                name="firstname"
                style={{ marginBottom: 20 }}
                rules={[
                  { required: true, message: "Please enter your first name" },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Write Here"
                  className="rounded-md"
                  style={{ height: "42px" }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-[#121212] text-[14px] font-medium">
                    Last Name *
                  </span>
                }
                name="lastname"
                style={{ marginBottom: 20 }}
                rules={[
                  { required: true, message: "Please enter your last name" },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Write Here"
                  className="rounded-md"
                  style={{ height: "42px" }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-[#121212] text-[14px] font-medium">
                    Phone
                  </span>
                }
                name="phone"
                style={{ marginBottom: 20 }}
              >
                <Input
                  size="large"
                  placeholder="Write Here"
                  className="rounded-md"
                  style={{ height: "42px" }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-[#121212] text-[14px] font-medium">
                    Email *
                  </span>
                }
                name="email"
                style={{ marginBottom: 20 }}
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Enter a valid email address" },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Write Here"
                  className="rounded-md"
                  style={{ height: "42px" }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-[#121212] text-[14px] font-medium">
                    Message*
                  </span>
                }
                name="message"
                style={{ marginBottom: 20 }}
                rules={[{ required: true, message: "Please leave a message" }]}
              >
                <Input.TextArea
                  rows={4}
                  className="resize-none rounded-md"
                  placeholder="Write Here"
                  style={{ minHeight: "120px" }}
                />
              </Form.Item>

              {/* Terms Agreement */}
              <Form.Item
                name="agree"
                valuePropName="checked"
                style={{ marginBottom: 24 }}
              >
                <Checkbox className="text-[#4A4C56] text-[13px] leading-relaxed">
                  I agree to the{" "}
                  <a href="/privacypolicy" className="text-[#007BFF] underline">
                    Privacy Policy
                  </a>
                </Checkbox>
              </Form.Item>

              {/* hCaptcha widget */}
              <Form.Item style={{ marginBottom: 20 }}>
                <HCaptcha
                  ref={captchaRef}
                  sitekey="231048d6-eb42-4a90-ba0e-7e3ed338da68"
                  onVerify={(token) => setCaptchaToken(token)}
                  onExpire={() => setCaptchaToken(null)}
                />
              </Form.Item>

              {/* Submit Button */}
              <div className="flex justify-center w-full">
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!agreed || !captchaToken}
                  loading={loading}
                  className="w-full bg-[#007BFF] hover:bg-[#0056b3] text-white px-12 rounded-md flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    height: "40px",
                    fontSize: "15px",
                    fontWeight: "500",
                  }}
                >
                  Send
                  <SendIcon className="text-white" />
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactWrapper;
